import Cookies from 'js-cookie'
import get from 'lodash/get'

import { fetch } from '../shared/fetch'
import { CONSENT_CHANGED_EVENT, getConsent } from './_cookie-consent'
import { dispatcher } from './dispatcher'

const CATEGORY_ANALYTICS = 'analytics'
const CATEGORY_MARKETING = 'marketing'

let listenerAdded = false
let cleanupInventoryPromise = null
let cleanupInventoryCache = null

/**
 * @param cookie
 * @return {null|{key: string, category: string}}
 */
function normalizeCookieDefinition(cookie) {
  if (!cookie || typeof cookie !== 'object') {
    return null
  }

  const key = typeof cookie.key === 'string' ? cookie.key.trim() : ''
  if (!key) {
    return null
  }

  const category = cookie.category === CATEGORY_MARKETING ? CATEGORY_MARKETING : CATEGORY_ANALYTICS

  return { key, category }
}

/**
 * @return {string}
 */
function getCleanupInventoryUrl() {
  return get(window, ['APP', 'cookie_consent', 'cleanup_inventory_url'], '')
}

async function getInventoryCookies() {
  if (Array.isArray(cleanupInventoryCache)) {
    return cleanupInventoryCache
  }

  if (cleanupInventoryPromise) {
    return cleanupInventoryPromise
  }

  const url = getCleanupInventoryUrl()
  if (!url) {
    cleanupInventoryCache = []
    return cleanupInventoryCache
  }

  cleanupInventoryPromise = fetch(url, { credentials: 'same-origin' })
    .then(async (response) => {
      if (!response || !response.ok) {
        return []
      }

      const data = await response.json().catch(() => ({}))
      const rawCookies = data && Array.isArray(data.cookies) ? data.cookies : []

      return rawCookies.map(normalizeCookieDefinition).filter((cookie) => cookie)
    })
    .catch(() => [])
    .then((cookies) => {
      cleanupInventoryCache = cookies
      return cookies
    })
    .finally(() => {
      cleanupInventoryPromise = null
    })

  return cleanupInventoryPromise
}

function getPathCandidates() {
  const path = window.location.pathname || '/'
  const segments = path.split('/').filter(Boolean)
  const paths = ['/']

  if (segments.length === 0) {
    return paths
  }

  for (let i = 1; i <= segments.length; i += 1) {
    paths.push(`/${segments.slice(0, i).join('/')}`)
  }

  return [...new Set(paths)]
}

function getDomainCandidates() {
  const hostname = window.location.hostname || ''
  if (!hostname || hostname === 'localhost') {
    return []
  }

  const domains = new Set([hostname, `.${hostname}`])
  const parts = hostname.split('.')

  if (parts.length >= 2) {
    const baseDomain = parts.slice(-2).join('.')
    domains.add(baseDomain)
    domains.add(`.${baseDomain}`)
  }

  return [...domains]
}

function removeCookieWithVariants(cookieKey) {
  const paths = getPathCandidates()
  const domains = getDomainCandidates()

  Cookies.remove(cookieKey)

  paths.forEach((path) => {
    Cookies.remove(cookieKey, { path })
  })

  domains.forEach((domain) => {
    Cookies.remove(cookieKey, { domain })
    paths.forEach((path) => {
      Cookies.remove(cookieKey, { path, domain })
    })
  })
}

function getCurrentCookieKeys() {
  const rawCookies = document.cookie || ''
  if (!rawCookies) {
    return []
  }

  return rawCookies
    .split(';')
    .map((cookiePart) => cookiePart.split('=')[0].trim())
    .filter((cookieKey) => cookieKey !== '')
}

function resolveCookieKeys(cookiePattern, cookieKeys) {
  if (!cookiePattern.endsWith('*')) {
    return [cookiePattern]
  }

  const prefix = cookiePattern.slice(0, -1)
  if (!prefix) {
    return []
  }

  return cookieKeys.filter((cookieKey) => cookieKey.startsWith(prefix))
}

async function cleanupCookiesForCategories(categories) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return
  }

  const deniedCategorySet = new Set(categories)
  const cookieKeys = getCurrentCookieKeys()
  const keysToRemove = new Set()

  const inventory = await getInventoryCookies()
  inventory.forEach((cookie) => {
    if (!deniedCategorySet.has(cookie.category)) {
      return
    }

    resolveCookieKeys(cookie.key, cookieKeys).forEach((cookieKey) => {
      keysToRemove.add(cookieKey)
    })
  })

  keysToRemove.forEach((cookieKey) => {
    removeCookieWithVariants(cookieKey)
  })
}

function getDeniedCategories(consent) {
  const denied = []

  if (!consent || consent.analytics !== true) {
    denied.push(CATEGORY_ANALYTICS)
  }

  if (!consent || consent.marketing !== true) {
    denied.push(CATEGORY_MARKETING)
  }

  return denied
}

function setupCookieConsentCleanup() {
  const currentConsent = getConsent()
  cleanupCookiesForCategories(getDeniedCategories(currentConsent))

  if (listenerAdded) {
    return
  }

  dispatcher.addListener(CONSENT_CHANGED_EVENT, (ev) => {
    const consent = ev && ev.consent ? ev.consent : null
    cleanupCookiesForCategories(getDeniedCategories(consent))
  })

  listenerAdded = true
}

export { setupCookieConsentCleanup }
